import { AsyncAutocomplete, BrowseViewResponseDto } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useConfirm } from 'material-ui-confirm';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { AuditorCompetence } from '@/common';
import { apiFetch, getMessageFromError } from '@/core';
import env from '@/env';
import { AuditorProposalCompItem } from '../../types';
import { deleteAuditorProposalComp } from '../../api/deleteAuditorProposalComp';
import { createAuditorProposalComp } from '../../api/createAuditorProposalComp';

const getCompetencesForCurrentAuditor = async ({
  page,
  pageSize,
  search,
}: {
  page: number;
  pageSize: number;
  search: string;
}): Promise<BrowseViewResponseDto<AuditorCompetence>> => {
  const fetchResponse = await apiFetch(
    `${env.VITE_API_URL}/pb/s/api/sqo-portal/auditor-comps/me?${new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search: search.toString(),
    })}`,
    { method: 'GET' }
  );

  const response = (await fetchResponse.json()) as BrowseViewResponseDto<AuditorCompetence>;
  return response;
};

type ComeptencesProps = {
  auditorProposalId: string;
  competences: AuditorProposalCompItem[];
  isFormEditable?: boolean;
};

const CompetencesEdit = ({ auditorProposalId, competences, isFormEditable }: ComeptencesProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const [showAddInput, setShowAddInput] = useState(false);
  const [selectedCompetence, setSelectedCompetence] = useState<AuditorCompetence | null>(null);
  const queryClient = useQueryClient();

  const { mutate: deleteMutation } = useMutation({
    mutationFn: deleteAuditorProposalComp,
    onSuccess: () => {
      queryClient.invalidateQueries(['auditor-proposal-id', auditorProposalId]);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const { mutate: addMutation } = useMutation({
    mutationFn: createAuditorProposalComp,
    onSuccess: () => {
      setSelectedCompetence(null);
      setShowAddInput(false);
      queryClient.invalidateQueries(['auditor-proposal-id', auditorProposalId]);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  return (
    <>
      <Box sx={{ mb: 2 }}>
        {competences.length > 0 ? (
          <List>
            {competences.map((competence) => (
              <ListItem
                sx={{ px: 0 }}
                key={competence.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    disabled={!isFormEditable}
                    onClick={() =>
                      confirm({
                        description: `${t('This will premamently delete', {
                          defaultValue: 'This will premamently delete',
                        })} ${competence.name}`,
                      }).then(() => deleteMutation({ id: competence.id }))
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <LightbulbIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.25rem' }}
                  secondaryTypographyProps={{ fontSize: '1rem' }}
                  primary={competence.name}
                  secondary={competence.type}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="subtitle1">
            {t('No competences', { defaultValue: 'No competences' })}
          </Typography>
        )}
      </Box>
      {showAddInput ? (
        <>
          <AsyncAutocomplete<AuditorCompetence>
            id="auditor-competence"
            label={t('Competence', { defaultValue: 'Competence' })}
            getData={async (search) => {
              const response = await getCompetencesForCurrentAuditor({
                page: 1,
                pageSize: 1000,
                search,
              });
              return response.results;
            }}
            getOptionLabel={(option) => option.name || 'N/A'}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            selectedOption={selectedCompetence}
            setSelectedOption={setSelectedCompetence}
            fullWidth
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              if (!selectedCompetence) {
                return;
              }

              addMutation({
                auditorProposalId,
                auditorCompId: selectedCompetence.id,
              });
            }}
            sx={{ mt: 2 }}
            fullWidth
          >
            {t('Add', { defaultValue: 'Add' })}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => setShowAddInput(false)}
            sx={{ mt: 1 }}
            fullWidth
          >
            {t('Cancel', { defaultValue: 'Cancel' })}
          </Button>
        </>
      ) : (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={!isFormEditable}
          onClick={() => setShowAddInput(true)}
          fullWidth
        >
          {t('Add new competence', { defaultValue: 'Add new competence' })}
        </Button>
      )}
    </>
  );
};

export default CompetencesEdit;
